"use client";

import { Button, Heading, Icon, Text } from "@afa-shared/afa-components";
import { formatVideoDuration } from "@utils/videoHelper/formatVideoDuration";
import clsx from "clsx";
import { KeyboardEvent, useState } from "react";

import styles from "./VideoCard.module.css";
import VideoPlayer from "./VideoPlayer";

export interface IVideoCardProps {
  videoUrl?: string;
  playing?: boolean;
  heading?: string;
  headingVariant?: "h2" | "h3" | "h4" | "h5" | "h6";
  videoTitle?: string;
  text?: any;
  linkText?: string;
  linkUrl?: string;
  linkVariant?: "link" | "button";
  reverse?: boolean;
  onCardClick?: (v?: any) => void;
  direction?: "row" | "column";
  removeBorder?: boolean;
  removePadding?: boolean;
  backgroundColor?: string;
  consentGivenForMarketing?: boolean;
  onAcceptMarketingConsent?: () => void;
  videoOverlayImg?: string;
  useDefaultImage?: boolean;
  className?: string;
}

const VideoCard = ({
  heading,
  headingVariant = "h2",
  videoTitle,
  text,
  videoUrl,
  playing,
  linkText,
  linkUrl,
  linkVariant = "link",
  reverse,
  direction,
  onCardClick,
  removeBorder = false,
  removePadding = false,
  backgroundColor = "",
  consentGivenForMarketing = false,
  onAcceptMarketingConsent,
  videoOverlayImg,
  useDefaultImage,
  className,
}: IVideoCardProps) => {
  const directionKey = direction ? (reverse ? `${direction}-reverse` : direction) : undefined;
  const classnames = clsx(styles["video-card"], className, directionKey && styles[directionKey], {
    [styles["no-border"]]: removeBorder,
    [styles["no-padding"]]: removePadding,
  });

  const [durationSeconds, setDurationSeconds] = useState<number | null>(null);
  const duration = formatVideoDuration(durationSeconds);

  const shouldShowVideoPlayer = consentGivenForMarketing && Boolean(videoUrl);
  const shouldShowConsentPrompt = !consentGivenForMarketing;
  const hasContentSection = Boolean(heading || text || (linkText && linkUrl));

  const handleConsentKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onAcceptMarketingConsent) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onAcceptMarketingConsent();
    }
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onCardClick) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onCardClick();
    }
  };

  return (
    <div className={classnames} style={{ backgroundColor }}>
      {shouldShowVideoPlayer && (
        <div className={styles["video-column"]}>
          <div className={styles["video-wrapper"]}>
            <VideoPlayer
              url={videoUrl as string}
              playing={playing}
              title={videoTitle}
              onDuration={setDurationSeconds}
            />
          </div>
          {duration && (
            <p className={styles["video-duration"]}>
              Längd på film: <span className={styles["video-duration-value"]}>{duration}</span>
            </p>
          )}
        </div>
      )}
      {shouldShowConsentPrompt && (
        <div
          className={styles["thumbnail-wrapper"]}
          onClick={onAcceptMarketingConsent}
          onKeyDown={handleConsentKeyDown}
          role={onAcceptMarketingConsent ? "button" : undefined}
          tabIndex={onAcceptMarketingConsent ? 0 : undefined}
        >
          {videoOverlayImg && (
            <img
              className={clsx({ [styles["default-image"]]: useDefaultImage })}
              src={videoOverlayImg}
              alt=""
            />
          )}
          <div className={styles["cookie-info"]}>
            <Text variant="span">
              För att spela videon behöver du acceptera cookies för marknadsföring.
            </Text>
            <Button buttonType="primary" label="Acceptera marknadsförings&shy;cookies" />
          </div>
        </div>
      )}
      {hasContentSection && (
        <div
          className={clsx(styles.content, { [styles["has-url"]]: linkUrl })}
          onClick={onCardClick}
          onKeyDown={handleCardKeyDown}
          role={onCardClick ? "button" : undefined}
          tabIndex={onCardClick ? 0 : undefined}
        >
          {(heading || text) && (
            <div className={styles["text-wrapper"]}>
              {heading && linkUrl ? (
                <a href={linkUrl} aria-label={heading}>
                  <Heading variant={headingVariant} removeMargin>
                    {heading}
                  </Heading>
                </a>
              ) : (
                <Heading variant={headingVariant} removeMargin children={heading} />
              )}
              {text && <Text variant="paragraph" children={text} />}
            </div>
          )}
          {linkText && linkUrl && (
            <div className={styles["link-wrapper"]} aria-hidden>
              <div className={clsx(styles["link-span"], linkVariant && styles[linkVariant])}>
                {linkVariant !== "button" && (
                  <Icon name="arrow_forward" size="sm" referenceSize="link" />
                )}
                <span>{linkText}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoCard;
